import { mapGetters, mapMutations, mapState } from 'vuex';

import * as C from './styles';

import { FilterInfo } from '@/components/Common/FilterInfo';

import PanelOptionMixin from '../mixin';
import DashboardFilterMixin from '@/mixins/dashboardFilter';
import iframeMixin from '@/mixins/iframe';

import FilterIcon from '@svgIcons/filter.svg';
import FilledFilterIcon from '@svgIcons/dashboard/filter-solid.svg';

import { isValueExist } from '@/utils/utils';
import {
    FILTER_KEYS,
    IFRAME_EVENT_MESSAGE,
    WIDGET_TYPES,
} from '@/utils/consts';
import { checkIsAnalyticSource, checkIsHidden } from '@/utils/dashboard';
import { getAccountKey } from '@/components/Dashboard/common/WidgetSettingPanel/index';

import { GRAY } from '@/styles/common.styled';

const { PIVOT_TABLE_ANALYTIC, AB_TESTING } = WIDGET_TYPES;
const { TITLE, STATUS, CHANNEL, ID, AD_ACCOUNT_ID, UTM, LABEL, OS, OBJECTIVE } =
    FILTER_KEYS;
const DISCARD_FIELDS = [
    TITLE,
    STATUS,
    CHANNEL,
    ID,
    AD_ACCOUNT_ID,
    UTM,
    LABEL,
    OS,
    OBJECTIVE,
];

export default {
    name: 'PanelDashboardFilterOption',
    mixins: [PanelOptionMixin, DashboardFilterMixin, iframeMixin],
    props: {
        title: {
            type: String,
        },
        originWidgetFilters: {
            type: Array,
        },
        widgetType: {
            type: String,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        selectedSource: {
            type: Array,
            default() {
                return [];
            },
        },
    },
    watch: {
        isWidgetFilterOpen(value) {
            if (!value) {
                this.closeSecondPanel();
            }
        },
    },
    computed: {
        ...mapState('newDashboard', ['isWidgetFilterOpen']),
        ...mapGetters('newDashboard', [
            'currentView',
            'currentViewConfig',
            'tableAdAccountsByAccountKey',
            'viewById',
        ]),
        genericAccounts() {
            const { selectedSource } = this;

            return R.filter(checkIsAnalyticSource)(selectedSource);
        },
        viewFilters() {
            const { editingWidget, currentViewConfig, widgetType } = this;

            return widgetType === AB_TESTING
                ? R.prop('viewGroupFilters', editingWidget)
                : R.prop('groupFilters', currentViewConfig);
        },
        discardFieldOptions() {
            const { selectedSource, widgetType } = this;

            if (widgetType === PIVOT_TABLE_ANALYTIC) {
                const hasChannelBreakdown = R.any(
                    R.pathSatisfies(Boolean, [
                        'settings',
                        'blendDataByBreakdown',
                        'channel',
                        'key',
                    ]),
                    selectedSource
                );

                return hasChannelBreakdown
                    ? R.without([CHANNEL], DISCARD_FIELDS)
                    : DISCARD_FIELDS;
            }

            return [AD_ACCOUNT_ID];
        },
    },
    methods: {
        ...mapMutations('newDashboard', ['setIsWidgetFilterOpen']),
    },
    render() {
        const {
            disabled,
            originWidgetFilters: widgetFilters,
            viewFilters,
        } = this;

        const hasWidgetFilter = isValueExist(widgetFilters);
        const hasViewFilter = isValueExist(viewFilters);

        return (
            <C.Wrapper>
                {/* widget filter */}
                {this.wrapOptions({
                    children: hasWidgetFilter ? (
                        <C.ContentWrapper>
                            <FilterInfo
                                groupFilters={widgetFilters}
                                genericBreakdownTranslator={this.translator}
                            />
                        </C.ContentWrapper>
                    ) : (
                        <C.EmptyContentWrapper />
                    ),
                    title: this.$t('NEW_DASHBOARD.widget_filter_option_title'),
                    icon: (
                        <FilterIcon
                            class="metric-icon"
                            v-view-box
                            fill={GRAY(80)}
                        />
                    ),
                    hasEditBtn: !disabled,
                    onClick: () => {
                        const isOpen = !this.isWidgetFilterOpen;

                        /**
                         * Widget adAccounts reflect the state at the selected moment,
                         * so we need to get the latest selected source from tableAdAccountsByAccountKey.
                         */
                        const latestSelectedSource = R.compose(
                            R.filter(isValueExist),
                            R.map((source) => {
                                return this.tableAdAccountsByAccountKey[
                                    getAccountKey(source)
                                ];
                            })
                        )(this.selectedSource);

                        const data = isOpen
                            ? {
                                  groupFilters: widgetFilters,
                                  widgetType: this.isAnalytic
                                      ? 'normal'
                                      : 'marketing',
                                  accounts: latestSelectedSource,
                                  currentView: this.currentView,
                                  isHiddenTeamspace: checkIsHidden(
                                      this.viewById(
                                          this.currentView?.teamspace?.id
                                      )
                                  ),
                                  isTemplateView: this.isTemplateView,
                              }
                            : null;

                        this.postIframeMessage({
                            origin: 'VUE',
                            type: IFRAME_EVENT_MESSAGE.TOGGLE_WIDGET_FILTER,
                            data,
                        });
                        this.setIsWidgetFilterOpen(isOpen);
                    },
                })}
                {/* dashboard filter */}
                {hasViewFilter &&
                    this.wrapOptions({
                        children: (
                            <C.ContentWrapper>
                                <FilterInfo
                                    groupFilters={viewFilters}
                                    genericBreakdownTranslator={this.translator}
                                />
                            </C.ContentWrapper>
                        ),
                        title: this.$t(
                            'NEW_DASHBOARD.dashboard_filter_option_title'
                        ),
                        icon: (
                            <FilledFilterIcon
                                class="metric-icon"
                                v-view-box
                                fill={GRAY(80)}
                            />
                        ),
                        hasEditBtn: false,
                        disableClick: true,
                    })}
            </C.Wrapper>
        );
    },
};
